import type { AnyRoute, LinkProps } from '@tanstack/react-router'

import type {
  AreaDefinition,
  AreaId,
  AreaRoute,
  AreaSection,
  NavigationItem,
  ProductDefinition,
  RoutedAreaId,
} from '@/lib/module-routes'
import type { PermissionKey } from '@/lib/permissions'
import type { HomeWidgetDefinition } from '@/lib/home-widgets'

import { listPlatformModules } from './platform-modules.generated'

type PlatformModule = ReturnType<typeof listPlatformModules>[number]
export type ModuleRoute = PlatformModule['routes'][number]
export type Product = ProductDefinition<Exclude<LinkProps['to'], undefined>>

// Read lazily, not at import time: a module barrel pulls in its route files,
// which import the area layout routes, which render the sidebar that reads this
// list. The cycle is fine once everything is initialized — reading during
// module evaluation is not (the exports are still undefined).
let platformModules: ReturnType<typeof listPlatformModules> | null = null

function modules() {
  platformModules ??= listPlatformModules()
  return platformModules
}

export function collectHomeWidgets(contributions: readonly { widgets?: readonly HomeWidgetDefinition[] }[]): readonly HomeWidgetDefinition[] {
  const widgets = contributions.flatMap((module) => module.widgets ?? [])
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
  const ids = new Set<string>()
  for (const widget of widgets) {
    if (ids.has(widget.id)) throw new Error(`Duplicate Home widget id: ${widget.id}`)
    ids.add(widget.id)
    if (widget.defaultSize.columns < widget.minimumSize.columns || widget.defaultSize.rows < widget.minimumSize.rows)
      throw new Error(`Home widget ${widget.id} is smaller than its minimum size`)
    if (widget.maximumSize && (widget.defaultSize.columns > widget.maximumSize.columns || widget.defaultSize.rows > widget.maximumSize.rows))
      throw new Error(`Home widget ${widget.id} is larger than its maximum size`)
  }
  return widgets
}

let widgetList: readonly HomeWidgetDefinition[] | null = null

export function homeWidgets(): readonly HomeWidgetDefinition[] {
  widgetList ??= collectHomeWidgets(modules())
  return widgetList
}

export function moduleRoutes(): ModuleRoute[] {
  return modules().flatMap((module): readonly AnyRoute[] => module.routes) as ModuleRoute[]
}

function optionsOf(route: AnyRoute): { id?: string; path?: string } {
  return route.options
}

export function composeRouteTree<Root extends AnyRoute>(root: Root, routes: readonly AnyRoute[]) {
  const known = new Set<AnyRoute>([root, ...routes])
  const children = new Map<AnyRoute, AnyRoute[]>()
  for (const route of routes) {
    const parent: AnyRoute | undefined = route.options.getParentRoute?.()
    if (!parent || !known.has(parent))
      throw new Error(`Route ${optionsOf(route).path ?? optionsOf(route).id} has no parent in the route tree`)
    children.set(parent, [...(children.get(parent) ?? []), route])
  }
  for (const [parent, list] of children) parent.addChildren(list)
  return root
}

let productList: readonly Product[] | null = null

export function products(): readonly Product[] {
  productList ??= modules()
    .flatMap((module): readonly ProductDefinition[] => module.products ?? [])
    .sort((a, b) => a.order - b.order) as Product[]
  return productList
}

export function productById(id: string): Product | undefined {
  return products().find((product) => product.id === id)
}

let areaList: readonly AreaDefinition[] | null = null

export function areas(): readonly AreaDefinition[] {
  areaList ??= modules().flatMap((module): readonly AreaDefinition[] => module.areas ?? [])
  return areaList
}

export function areaById(id: string): AreaDefinition {
  const area = areas().find((candidate) => candidate.id === id)
  if (!area) throw new Error(`No module declares an area named ${id}`)
  return area
}

export function areaRoute<A extends RoutedAreaId>(id: A): AreaRoute<A> {
  const { route } = areaById(id)
  if (!route) throw new Error(`Area ${id} has no layout route`)
  return route as AreaRoute<A>
}

const sectionsByArea = new Map<AreaId, readonly AreaSection[]>()

export function areaSections(area: AreaId): readonly AreaSection[] {
  let sections = sectionsByArea.get(area)
  if (!sections) {
    sections = modules()
      .flatMap((module): readonly AreaSection[] => module.sections?.[area] ?? [])
      .sort((a, b) => a.order - b.order)
    sectionsByArea.set(area, sections)
  }
  return sections
}

const itemsByArea = new Map<AreaId, readonly NavigationItem[]>()

export function navigationItems(area: AreaId): readonly NavigationItem[] {
  let items = itemsByArea.get(area)
  if (!items) {
    const sectionOrder = areaSections(area).map((section) => section.id)
    items = modules()
      .flatMap((module): readonly NavigationItem[] => module.navigation?.[area] ?? [])
      .sort(
        (a, b) =>
          sectionOrder.indexOf(a.section) - sectionOrder.indexOf(b.section) ||
          a.order - b.order,
      )
    itemsByArea.set(area, items)
  }
  return items
}

function withoutTrailingSlash(path: string) {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}

function matchLength(pattern: string, leaf: string, exact: boolean) {
  if (leaf === pattern) return pattern.length
  return !exact && leaf.startsWith(`${pattern}/`) ? pattern.length : -1
}

export function activeNavigationItem(
  area: AreaId,
  leafFullPath: string | undefined,
): NavigationItem | undefined {
  return longestNavigationMatch(navigationItems(area), leafFullPath)
}

export function navigationItemForLeaf(leafFullPath: string | undefined): NavigationItem | undefined {
  return longestNavigationMatch(
    areas().flatMap((area) => navigationItems(area.id as AreaId)),
    leafFullPath,
  )
}

function longestNavigationMatch(
  items: readonly NavigationItem[],
  leafFullPath: string | undefined,
): NavigationItem | undefined {
  if (leafFullPath === undefined) return undefined
  const leaf = withoutTrailingSlash(leafFullPath)
  let best: { item: NavigationItem; length: number } | undefined
  for (const item of items) {
    const length = Math.max(
      matchLength(String(item.to), leaf, item.exact ?? false),
      ...(item.activeOn ?? []).map((path) => matchLength(String(path), leaf, true)),
    )
    if (length >= 0 && (!best || length > best.length)) best = { item, length }
  }
  return best?.item
}

export function navigationHref(item: NavigationItem, entityId: string) {
  return String(item.to).replace('$entityId', entityId)
}

export function productEntry(
  product: Product,
  can: (permission: PermissionKey) => boolean,
): NavigationItem | undefined {
  return areas()
    .filter((area) => area.product === product.id)
    .flatMap((area) => navigationItems(area.id as AreaId))
    .find((item) => !item.permission || can(item.permission))
}

export function canAccessProduct(
  product: Product,
  can: (permission: PermissionKey) => boolean,
) {
  return productEntry(product, can) !== undefined
}

let productByFullPath: Map<string, Product> | null = null

function mapProductsByFullPath() {
  const rootedBy = new Map<AnyRoute, Product>(
    products().flatMap((product) => product.routes.map((route) => [route, product] as const)),
  )
  const productOf = (route: AnyRoute | undefined): Product | undefined =>
    route && (rootedBy.get(route) ?? productOf(route.options.getParentRoute?.()))

  return new Map(
    moduleRoutes().flatMap((route: AnyRoute) => {
      const product = productOf(route)
      return product && optionsOf(route).path
        ? [[withoutTrailingSlash(route.fullPath), product] as const]
        : []
    }),
  )
}

export function productForLeaf(leafFullPath: string | undefined) {
  if (leafFullPath === undefined) return undefined
  productByFullPath ??= mapProductsByFullPath()
  return productByFullPath.get(withoutTrailingSlash(leafFullPath))
}
